import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';
import preferences from 'Preferences';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig, isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import APIUtil from 'util/APIUtil';

import {
  ProcessTaskFileStateQuery,
  ProcessTaskFileStateQueryVariables,
} from './graphql/__generated/ProcessTaskFileStateQuery';
import {PROCESS_TASK_FILE_STATE_QUERY} from './graphql/GetProcessTask';
import ProcessTaskDetailsOutputsTabState from './ProcessTaskDetailsOutputsTabState';
import ProcessTaskDetailsTabState from './ProcessTaskDetailsTabState';
import ProcessTaskFlowchartTabState from './ProcessTaskFlowchartTabState';

type ProcessTask = ProcessTaskFileStateQuery['processTask'];

type ProcessTaskSchema = ExtractByTypename<ProcessTask, 'ProcessTaskNode'>['schema'];

interface ProcessTaskFileStateAttributes {
  processTask?: ProcessTask;
  name?: string;
  url?: string;
  schema?: ProcessTaskSchema;
  executionFlowchartId?: string | null;
  procedureMethodTaskId?: string;
  rootTaskId?: string;
  archiveRecord?: ArchiveRecord | null;
  missingFlowchartMessage?: string;
}

class ProcessTaskFileState extends ApolloFileState<
  ProcessTaskFileStateQuery,
  ProcessTaskFileStateQueryVariables,
  ProcessTaskFileStateAttributes
> {
  static fileType = ModelTypeConstants.PROCESS_TASK;
  static override getQueryOptions(fileId: string): QueryOptions<ProcessTaskFileStateQueryVariables> {
    return {
      query: PROCESS_TASK_FILE_STATE_QUERY,
      variables: {
        taskId: fileId,
      },
    };
  }

  static override dataToAttributes(data: ProcessTaskFileStateQuery): ProcessTaskFileStateAttributes {
    const {processTask} = data;
    if (isInaccessible(processTask)) {
      return {};
    }

    // The flowchart tab assumes a single root, reading the first entry of ``rootTasks`` (the deprecated
    // singular ``rootTask`` field it replaced). This is reasonable because workflow tasks should only ever
    // have one root (there is no split/pool), and procedure tasks should eventually stop showing this
    // flowchart tab altogether (we are removing the task details pages for procedure tasks).
    let executionFlowchartId: string | undefined;
    let rootTaskId: string | undefined;
    let procedureMethodTaskId: string | undefined;
    let missingFlowchartMessage: string | undefined;
    const rootTask = processTask.rootTasks[0];
    if (rootTask != null) {
      executionFlowchartId = rootTask.executionFlowchart?.id;
      rootTaskId = rootTask.id;
    } else if (
      processTask.procedureStepExecutionInstance != null &&
      !isInaccessible(processTask.procedureStepExecutionInstance)
    ) {
      const methodExecutionInstance = processTask.procedureStepExecutionInstance?.methodExecutionInstance;
      if (methodExecutionInstance.methodTask != null) {
        const methodTask = methodExecutionInstance?.methodTask;
        if (!isInaccessible(methodTask)) {
          const methodRootTask = methodTask.rootTasks[0];
          executionFlowchartId = methodRootTask?.executionFlowchart?.id;
          rootTaskId = methodRootTask?.id;
          procedureMethodTaskId = methodTask.id;
        }
      } else if (methodExecutionInstance.templateMethodVersion != null) {
        const missingNodeName = isInaccessible(methodExecutionInstance.templateMethodVersion)
          ? NO_ACCESS_TEXT
          : methodExecutionInstance.templateMethodVersion.name;

        missingFlowchartMessage = `This flowchart will be displayed when the "${missingNodeName}" unit operation is started.`;
      }
    } else {
      executionFlowchartId = processTask.executionFlowchart?.id;
      rootTaskId = isFlowchartType(processTask.schema.executionType) ? processTask.id : undefined;
    }

    return {
      processTask,
      name: processTask.name,
      url: processTask.url,
      schema: processTask.schema,
      rootTaskId,
      procedureMethodTaskId,
      executionFlowchartId: executionFlowchartId ?? null,
      archiveRecord: processTask.archiveRecord,
      missingFlowchartMessage,
    };
  }

  static override openFile(fileId: string): JQueryPromise<ProcessTaskFileState> {
    return super.openFile(fileId) as JQueryPromise<ProcessTaskFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  setupPrimaryBufferTab(): void {
    const processTaskDetailsTab = new ProcessTaskDetailsTabState({
      processTaskId: this.id,
    });

    this.buffers().at(0).tabs().add(processTaskDetailsTab);
    processTaskDetailsTab.set({active: true});
  }

  setupSecondaryBufferTab(): void {
    const tabs = [];

    if (this.shouldDisplayOutputTab()) {
      tabs.push(new ProcessTaskDetailsOutputsTabState({processTaskId: this.id}));
    }

    if (this.shouldDisplayFlowchartTab()) {
      tabs.push(
        new ProcessTaskFlowchartTabState({
          taskId: this.id,
          rootTaskId: this.rootTaskId(),
          procedureMethodTaskId: this.get('procedureMethodTaskId'),
          missingFlowchartMessage: this.missingFlowchartMessage(),
        })
      );
    }

    if (tabs.length > 0) {
      tabs.forEach((tab) => {
        this.buffers().at(1).tabs().add(tab);
      });
      const splitPref = preferences.get(this.getSplitWorkspacePreferenceKey());
      this.setIsWorkspaceSplit(splitPref != null ? splitPref : true);
    } else {
      this.set('statusbar', new statusbar.SingleBufferStatusBarState());
    }
  }

  shouldDisplayFlowchartTab(): boolean {
    return (
      (this.executionFlowchartId() != null && this.rootTaskId() != null) ||
      this.missingFlowchartMessage() != null
    );
  }

  shouldDisplayOutputTab(): boolean {
    return this.hasOutputSchema() && this.supportsVisibleOutputs();
  }

  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState(), new BufferState()]);
    this.set({buffers});

    this.setupPrimaryBufferTab();
    this.setupSecondaryBufferTab();
  }

  override editURL(): string | undefined {
    return this.get('url');
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set({archiveRecord});
  }

  hasOutputSchema(): boolean {
    return this.get('schema')?.outputSchema != null;
  }

  supportsVisibleOutputs(): boolean {
    const executionType = this.get('schema')?.executionType;
    const schemaName = this.get('schema')?.name;
    if (executionType != null && schemaName != null) {
      return getProcessPlatformFeatureConfig(
        ProcessPlatformFeatureType.ARE_OUTPUTS_VISIBLE,
        executionType,
        schemaName
      );
    }
    return false;
  }

  missingFlowchartMessage(): string | null {
    return this.get('missingFlowchartMessage') ?? null;
  }

  executionFlowchartId(): string | null {
    return this.get('executionFlowchartId') ?? null;
  }

  rootTaskId(): string | null {
    return this.get('rootTaskId') ?? null;
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }

  activateOutputsTab(): void {
    this.activateTab(ProcessTaskDetailsOutputsTabState);
  }

  activateTaskDetailsTab(): void {
    this.activateTab(ProcessTaskDetailsTabState);
  }

  activateTabBasedOnSourceId(sourceId: string): void {
    if (APIUtil.isProcessTask(sourceId)) {
      this.activateTaskDetailsTab();
    } else if (APIUtil.isProcessOutput(sourceId)) {
      this.activateOutputsTab();
    }
  }
}

export default ProcessTaskFileState;
